import React, {useCallback} from 'react';
import {Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
    NumberVisibilityMode,
    NumberVisibilityModes,
    useNumberVisibilityMode,
} from '../../hooks/use-number-visibility-mode';

const TOGGLE_BUTTON_HEIGHT = 30;
const TOGGLE_TOOLBAR_STACK_SX = {
    border: '1px solid transparent',
    borderColor: 'divider',
    p: 0.5,
    borderRadius: 1,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};
const TOGGLE_TOOLBAR_GROUP_SX = {
    '& .MuiToggleButtonGroup-grouped': {
        borderRadius: '0.5rem',
        height: TOGGLE_BUTTON_HEIGHT,
        minWidth: TOGGLE_BUTTON_HEIGHT,
        px: 1,
        border: 'none',
    },
    '& .MuiToggleButtonGroup-grouped:not(t => t.:irstof-type)': {
        marginLeft: '.25rem',
    },
};

/**
 * Page-level toggle for compact (analytics) vs precise (report) number formatting.
 */
const NumberVisibilityModeToggle: React.FC = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.numberVisibilityMode'});
    const {mode, setMode, loading} = useNumberVisibilityMode();

    /**
     * Persists the selected number visibility mode. Ignores deselect (null) from exclusive groups.
     */
    const handleChange = useCallback((_: React.MouseEvent<HTMLElement>, value: NumberVisibilityMode | null) => {
        if (!value) {
            return;
        }
        setMode(value);
    }, [setMode]);

    return (
        <Stack sx={TOGGLE_TOOLBAR_STACK_SX}>
            <ToggleButtonGroup
                disabled={loading}
                exclusive
                onChange={handleChange}
                size={'small'}
                sx={TOGGLE_TOOLBAR_GROUP_SX}
                value={mode}
                aria-label={t(t => t.groupAriaLabel)}
            >
                <ToggleButton
                    value={NumberVisibilityModes.compact}
                    aria-label={t(t => t.overallTooltip)}
                    sx={{backgroundColor: mode === NumberVisibilityModes.compact ? 'divider' : 'transparent'}}
                >
                    <Tooltip title={t(t => t.overallTooltip)}>
                        <Typography variant={'caption'} fontWeight={600} lineHeight={1}>
                            {t(t => t.compactLabel)}
                        </Typography>
                    </Tooltip>
                </ToggleButton>
                <ToggleButton
                    value={NumberVisibilityModes.precise}
                    aria-label={t(t => t.accurateTooltip)}
                    sx={{backgroundColor: mode === NumberVisibilityModes.precise ? 'divider' : 'transparent'}}
                >
                    <Tooltip title={t(t => t.accurateTooltip)}>
                        <Typography variant={'caption'} fontWeight={600} lineHeight={1}>
                            {t(t => t.preciseLabel)}
                        </Typography>
                    </Tooltip>
                </ToggleButton>
            </ToggleButtonGroup>
        </Stack>
    );
};

export default NumberVisibilityModeToggle;
